import { useCallback, useContext, useMemo, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuthz } from '@ohpen/react-authz';

import { optionsGetAllOptionsForFields, usersGetAll } from '@pmp/api';
import { getFieldsOfKind, PMP_APPLICATION_ID } from '@pmp/components';
import {
  buildFlagDescriptions,
  buildPermissionCatalogue,
  buildRoleCatalogue,
  isPermission,
  PmpConfigContext,
  useHostChrome,
} from '@pmp/contexts';
import { buildCustomFields, buildParameters } from '@pmp/utils';
import { ConfigApp } from '@config';

/**
 * What PMP hands the configuration module. Mounted only on the configuration route, so the
 * catalogues are built when somebody opens it rather than on every page of the portal.
 */
export const ConfigHost = (): ReactElement => {
  const { t } = useTranslation();
  const { userPermissions } = useAuthz();
  const pmpConfig = useContext(PmpConfigContext);
  const {
    devClient: { selectedClient, clientSelect },
    aboutInfo,
    userAccount,
  } = useHostChrome();

  // Permissions are declared in this application's code, so the module is handed the catalogue.
  const permissionCatalogue = useMemo(() => buildPermissionCatalogue(t), [t]);
  const roleCatalogue = useMemo(() => buildRoleCatalogue(t, pmpConfig?.groupPermissions), [pmpConfig?.groupPermissions, t]);
  const flagDescriptions = useMemo(() => buildFlagDescriptions(t), [t]);
  const customFields = useMemo(() => buildCustomFields(t, pmpConfig?.additionalFields), [pmpConfig?.additionalFields, t]);
  const getParameters = useCallback(() => buildParameters(t, pmpConfig ?? undefined), [pmpConfig, t]);

  // The module reads every list through the host rather than owning a client of its own. Which
  // fields belong to each kind is PMP's knowledge, so the module only asks for one or the other.
  const getValueLists = useCallback(async (kind: 'fixed' | 'entity') => {
    const { data } = await optionsGetAllOptionsForFields({ query: { fields: getFieldsOfKind(kind) } });
    return data?.fields ?? {};
  }, []);

  // Each configuration section is a resource of its own, named after the section it guards, so a
  // section the account cannot view is neither routed nor listed in the module's side bar.
  const canViewSection = useCallback(
    (sectionKey: string) => {
      const permission = `config${sectionKey.charAt(0).toUpperCase()}${sectionKey.slice(1)}:view`;
      return isPermission(permission) && userPermissions.has(permission);
    },
    [userPermissions],
  );

  const getUsers = useCallback(async () => {
    const { data } = await usersGetAll();
    return data ?? [];
  }, []);

  return (
    <>
      {userAccount.modal}
      <ConfigApp
        application={PMP_APPLICATION_ID}
        applicationName={t('global.title')}
        client={selectedClient}
        customMenu={clientSelect}
        onClickMyAccount={userAccount.open}
        aboutInfo={aboutInfo}
        configuration={pmpConfig}
        getValueLists={getValueLists}
        permissionCatalogue={permissionCatalogue}
        roleCatalogue={roleCatalogue}
        flagDescriptions={flagDescriptions}
        customFields={customFields}
        getParameters={getParameters}
        getUsers={getUsers}
        canViewSection={canViewSection}
      />
    </>
  );
};
